/**
 * ZydusMart Notify Me Feature
 * Handles back-in-stock notifications for logged-in users
 * Flow: CTA click → slide-open form (pre-filled, read-only) → submit → de-dup → API → success message
 */

window.NotifyMe = window.NotifyMe || (function () {
    'use strict';

    // Configuration
    const CONFIG = {
        API_URL: 'https://zydus.binaryic.in/api/notify-me',
        API_URL_GUEST: 'https://zydus.binaryic.in/api/notify-me-guest',
        GET_NOTIFIED_PRODUCTS_URL: 'https://zydus.binaryic.in/api/get-notified-products',
        GET_NOTIFIED_PRODUCTS_URL_GUEST: 'https://zydus.binaryic.in/api/get-notified-products-guest',
        FEEDBACK_DURATION: 5000 // 5 seconds
    };

    const STORAGE_KEYS = {
        GUEST_DATA: '_zydus_notify_me_guest_data',
        GUEST_SESSION_ID: '_zydus_guest_session_id',
        GUEST_VARIANT_IDS: '_zydus_notify_me_guest_variant_ids'
    };

    // State
    let isProcessing = false;
    let subbedVariantIds = []; // Cache of variant IDs the user is already subscribed to

    function isGuestCustomer() {
        return !window.__ZYDUS_CUSTOMER__ || !window.__ZYDUS_CUSTOMER__.id;
    }

    function redirectGuestToLogin(button) {
        const currentUrl = window.location.href;
        // Save the full intent so we can auto-subscribe after login
        const intent = {
            productId: (button && button.dataset) ? (button.dataset.productId || '') : '',
            variantId: (button && button.dataset) ? (button.dataset.variantId || '') : '',
            sku:       (button && button.dataset) ? (button.dataset.sku       || '') : '',
            pageUrl:   currentUrl
        };
        localStorage.setItem('_zydus_notify_me_pending_intent', JSON.stringify(intent));
        localStorage.setItem('currentURL', currentUrl); // kept for compatibility
        window.location.href = `/account/login?return_url=${encodeURIComponent(currentUrl)}`;
    }

    /**
     * After a login redirect, check if there is a pending Notify Me intent saved
     * before the redirect.  If the user is now logged in and we are on the same
     * product page, auto-fire the subscription without requiring another button press.
     *
     * Flow: guest clicks → intent saved + redirect to login → login → Shopify returns
     * user to product page → init() calls this → subscription fires automatically.
     */
    async function checkAndFirePendingIntent() {
        // Only act once the user is actually logged in
        if (isGuestCustomer()) return;

        const raw = localStorage.getItem('_zydus_notify_me_pending_intent');
        if (!raw) return;

        let intent;
        try {
            intent = JSON.parse(raw);
        } catch (e) {
            localStorage.removeItem('_zydus_notify_me_pending_intent');
            return;
        }

        const { productId, variantId, sku, pageUrl } = intent;
        if (!productId || !variantId) {
            localStorage.removeItem('_zydus_notify_me_pending_intent');
            return;
        }

        // Only auto-fire on the exact product page the user originally clicked from
        const currentPagePath = window.location.pathname;
        let intentPagePath = '';
        try { intentPagePath = pageUrl ? new URL(pageUrl).pathname : ''; } catch (_) { intentPagePath = pageUrl || ''; }

        if (intentPagePath && currentPagePath !== intentPagePath) {
            console.log('[NotifyMe] Pending intent found but on a different page — keeping for next load', { currentPagePath, intentPagePath });
            return;
        }

        console.log('[NotifyMe] 🔔 Pending intent detected after login — auto-subscribing:', intent);

        // Clear BEFORE the API call to prevent double-fire on page reload
        localStorage.removeItem('_zydus_notify_me_pending_intent');
        localStorage.removeItem('currentURL');

        // Read customer details from the global object set by theme.liquid
        const customer = window.__ZYDUS_CUSTOMER__ || {};
        const name  = customer.name  || '';
        const email = customer.email || '';
        const phone = customer.phone || '';

        // Locate the notify-me-form for this variant (used by processSubscription/closeForm)
        const form = document.getElementById(`notify-me-form-${variantId}`)
                  || document.querySelector('.notify-me-form');

        // Show a loading spinner on the Notify Me button while we work
        const notifyBtn = document.querySelector(`.notify-me-btn[data-variant-id="${variantId}"]`);
        if (notifyBtn) setButtonLoading(notifyBtn, true);

        // Small delay so the page can fully render buttons, containers, etc.
        await new Promise(resolve => setTimeout(resolve, 800));

        await processSubscription({
            productId,
            variantId,
            sku,
            name,
            email,
            phone,
            isGuest:    false,
            customerId: (form && form.dataset.customerId) ? form.dataset.customerId : '',
            submitBtn:  notifyBtn || document.querySelector('.notify-me-submit-btn'),
            form:       form,
            container:  null
        });

        if (notifyBtn) setButtonLoading(notifyBtn, false);
        console.log('[NotifyMe] ✅ Pending intent processed and cleared');
    }

    /**
     * Initialize the Notify Me functionality
     */
    function init() {
        console.log('[NotifyMe] Initializing...');

        // Set initial button visibility based on variant availability
        setInitialButtonVisibility();

        // Check for existing subscriptions to show "Already Subscribed" messages
        fetchSubscribedProducts();

        // Auto-fire any Notify Me subscription intent saved before the login redirect
        checkAndFirePendingIntent();

        // Use capture:true so this fires BEFORE anchor/card link handlers in the bubble phase.
        // This prevents the product card from navigating to the PDP when "Notify Me" is clicked on PLP.
        document.body.addEventListener('click', (event) => {
            const button = event.target.closest('.notify-me-btn');
            if (button) {
                handleCTAClick(event);
            }
        }, { capture: true });

        // Attach event listeners to all Notify Me submit buttons (inside form)
        // These are inside a persistent form, so we can keep direct listeners or use delegation
        attachSubmitListeners();
        attachNativeValidationListeners();
        syncAllSubmitStates();

        // Attach event listeners to the new close buttons
        attachCloseListeners();

        // Prevent clicks on form inputs/labels from bubbling to card-level navigation handlers.
        // NO preventDefault here — inputs still need to receive focus and typing.
        document.body.addEventListener('click', (event) => {
            if (event.target.closest('.notify-me-form') &&
                event.target.matches('input, textarea, select, label, .notify-me-form__inner')) {
                event.stopImmediatePropagation();
            }
        }, { capture: true });

        // Listen for variant changes
        observeVariantChanges();

        // Re-sync Notify Me state when the browser restores a page from back/forward cache.
        attachPageShowListener();
    }

    /**
     * Attach click listeners to CTA "Notify Me" buttons (legacy - replaced by delegation)
     */
    function attachCTAListeners() {
        // No longer used, handled by delegation in init()
    }

    /**
     * Attach click listener for submit buttons via delegation on document.body.
     * Uses CAPTURE phase so it fires before any anchor/card navigation handlers.
     */
    function attachSubmitListeners() {
        document.body.addEventListener('click', (event) => {
            const submitBtn = event.target.closest('.notify-me-submit-btn');
            if (submitBtn) {
                event.preventDefault();
                event.stopImmediatePropagation();
                handleFormSubmit(event);
            }
        }, { capture: true });
        console.log('[NotifyMe] Submit listener (capture) attached via delegation');
    }

    /**
     * Keep native validation styling in sync with the current form state.
     */
    function attachNativeValidationListeners() {
        document.body.addEventListener('input', (event) => {
            const input = event.target.closest('.notify-me-form .form-control');
            if (!input) return;

            const form = input.closest('.notify-me-form');
            if (!form) return;

            if (input.classList.contains('is-touched')) {
                syncFieldValidationState(input);
            }
            syncSubmitState(form);
        }, true);

        document.body.addEventListener('blur', (event) => {
            const input = event.target.closest('.notify-me-form .form-control');
            if (!input) return;

            const form = input.closest('.notify-me-form');
            if (!form) return;

            input.classList.add('is-touched');
            syncFieldValidationState(input);
            syncSubmitState(form);
        }, true);

        document.body.addEventListener('invalid', (event) => {
            const input = event.target.closest('.notify-me-form .form-control');
            if (!input) return;

            const form = input.closest('.notify-me-form');
            if (!form) return;

            event.preventDefault();
            input.classList.add('is-touched');
            syncFieldValidationState(input);
            syncSubmitState(form);
        }, true);

        console.log('[NotifyMe] Native validation listeners attached');
    }

    /**
     * Attach click listener for close buttons via delegation on document.body.
     * Uses CAPTURE phase so it fires before any anchor/card navigation handlers.
     */
    function attachCloseListeners() {
        document.body.addEventListener('click', (event) => {
            const closeBtn = event.target.closest('.notify-me-close');
            if (!closeBtn) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            // Find the form this close button belongs to
            const form = closeBtn.closest('.notify-me-form');
            const ctaBtn = form ? form.closest('.button-price-area, .boost-sd__product-item')?.querySelector('.notify-me-btn') : null;

            if (form) {
                closeForm(form, ctaBtn);
            }
        }, { capture: true });
        console.log('[NotifyMe] Close listener (capture) attached via delegation');
    }

    /**
     * Handle CTA button click — toggle the inline form open/closed
     */
    function handleCTAClick(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }

        const button = event ? event.target.closest('.notify-me-btn') : null;
        if (!button) return;

        if (isGuestCustomer()) {
            console.log('[NotifyMe] Guest clicked Notify Me - saving intent and redirecting to login');
            redirectGuestToLogin(button);
            return;
        }

        // ── DEBUG: print clicked button + full parent chain ──────────────
        console.group('[NotifyMe] 🔔 Notify Me button CLICKED');
        console.log('Button element:', button);
        console.log('Button data:', {
            productId: button.dataset.productId,
            variantId: button.dataset.variantId,
            sku: button.dataset.sku,
            ariaControls: button.getAttribute('aria-controls')
        });
        // Walk up the DOM and print each parent
        let el = button.parentElement;
        let depth = 0;
        while (el && depth < 10) {
            console.log(`Parent[${depth}]:`, el.tagName, el.id ? '#'+el.id : '', el.className ? '.'+[...el.classList].join('.') : '');
            el = el.parentElement;
            depth++;
        }
        console.groupEnd();
        // ── END DEBUG ─────────────────────────────────────────────────────

        if (button.classList.contains('subscribed')) {
            showFeedback(button, 'You are already subscribed! We will notify you when this item is back in stock.', 'info');
            return;
        }

        const container = button.closest('.notify-me-container');
        const variantId = button.dataset.variantId || (container ? container.dataset.variantId : null);
        let form = getSiblingForm(button);

        console.log('[NotifyMe] getSiblingForm result:', form);

        if (!form) {
            console.error('[NotifyMe] ❌ No form found for this button — form is NULL. Check aria-controls ID matches form id in DOM.');
            return;
        }

        // ── DEBUG: force add is-open so we can verify the form exists ────
        console.log('[NotifyMe] ✅ Form found:', form.id, '| is-open before:', form.classList.contains('is-open'));
        // ── END DEBUG ─────────────────────────────────────────────────────

        const isGuest = form.dataset.isGuest === 'true';
        const isOpen = form.classList.contains('is-open');

        if (!isGuest && !isOpen) {
            const name = container ? (container.dataset.customerName || '') : (window.__ZYDUS_CUSTOMER__ ? window.__ZYDUS_CUSTOMER__.name : '');
            const email = container ? (container.dataset.customerEmail || '') : (window.__ZYDUS_CUSTOMER__ ? window.__ZYDUS_CUSTOMER__.email : '');
            const phone = container ? (container.dataset.customerPhone || '') : (window.__ZYDUS_CUSTOMER__ ? window.__ZYDUS_CUSTOMER__.phone : '');

            if (!container) {
                console.log('[NotifyMe] handleCTAClick — no .notify-me-container found, building virtualContainer from window.__ZYDUS_CUSTOMER__', { name, email, phone });
            }
            const virtualContainer = container || {
                dataset: {
                    customerName: name,
                    customerEmail: email,
                    customerPhone: phone
                }
            };

            if (name && email && phone) {
                syncFormFields(form, virtualContainer);
                if (!validateNativeForm(form, form.querySelector('.notify-me-submit-btn'))) {
                    openForm(form, button, container);
                    return;
                }
                console.log('[NotifyMe] Complete profile data found - triggering direct submit via virtualContainer');
                handleDirectSubmit(button, virtualContainer, form);
                return;
            }
            console.log('[NotifyMe] Missing profile data - falling back to form open', { name, email, phone });
        }

        if (isOpen) {
            console.log('[NotifyMe] Form is already open — closing');
            closeForm(form, button);
        } else {
            console.log('[NotifyMe] Opening form now...');
            openForm(form, button, container);
            console.log('[NotifyMe] Form has is-open class after openForm():', form.classList.contains('is-open'));
        }

        syncSubmitState(form);
    }

    /**
     * Public method to open the Notify Me form manually (for Boost SD, etc.)
     */
    function openForVariant(productId, variantId, sku, ctaButton) {
        console.log('[NotifyMe] Opening for variant:', { productId, variantId, sku });

        if (isGuestCustomer()) {
            console.log('[NotifyMe] Guest openForVariant request - saving intent and redirecting to login');
            redirectGuestToLogin({ dataset: { productId: String(productId), variantId: String(variantId), sku: String(sku || '') } });
            return;
        }

        const form = getSiblingForm(ctaButton || variantId);
        if (!form) return;

        // Reset any existing feedback
        const feedback = getSiblingFeedback();
        if (feedback) feedback.style.display = 'none';

        // We need a dummy button or an existing one to associate with the form open state
        // For Boost SD, we can just use the clicked button if we pass it, 
        // but for now let's just ensure the form has the right data traits.
        const submitBtn = form.querySelector('.notify-me-submit-btn');
        if (submitBtn) {
            submitBtn.dataset.productId = productId;
            submitBtn.dataset.variantId = variantId;
            submitBtn.dataset.sku = sku;
        }

        openForm(form, { setAttribute: () => { } }, null); // dummy button object
    }

    /**
     * Get the notify-me-form element.
     * Priorities:
     *  1. FIRST: DOM proximity — look inside the same .notify-me-card-widget parent.
     *     This is immune to Swiper clone duplicate-ID problems (getElementById always
     *     returns the first match globally, which is the original slide, not the clone).
     *  2. If ctaButton has aria-controls, use getElementById as fallback (PDP / Boost SD).
     *  3. Look for sibling form in known card containers.
     *  4. Fallback to global form.
     */
    function getSiblingForm(ctaButtonOrId) {
        console.group('[NotifyMe] getSiblingForm lookup');

        // If it's a string, treat as an ID directly (Boost SD openForVariant path)
        if (typeof ctaButtonOrId === 'string') {
            const byId = document.getElementById(`notify-me-form-${ctaButtonOrId}`);
            console.log('String lookup notify-me-form-' + ctaButtonOrId + ':', byId);
            console.groupEnd();
            if (byId) return byId;
        }

        if (ctaButtonOrId && typeof ctaButtonOrId === 'object' && ctaButtonOrId.nodeType === 1) {

            // ── Priority 1: DOM proximity within the same .notify-me-card-widget ──
            // Safe against Swiper clone duplicate IDs — uses DOM traversal, not getElementById
            const widget = ctaButtonOrId.closest('.notify-me-card-widget');
            if (widget) {
                const localForm = widget.querySelector('.notify-me-form');
                console.log('[Priority 1] Same .notify-me-card-widget form:', localForm);
                if (localForm) {
                    console.groupEnd();
                    return localForm;
                }
            }

            // ── Priority 2: aria-controls getElementById (PDP / Boost SD) ──
            const targetId = ctaButtonOrId.getAttribute('aria-controls');
            console.log('aria-controls value:', targetId);
            if (targetId) {
                const targetForm = document.getElementById(targetId);
                console.log('document.getElementById("' + targetId + '"):', targetForm);
                if (targetForm) {
                    console.groupEnd();
                    return targetForm;
                }
                console.warn('⚠️ aria-controls ID exists but element not found in DOM!');
            } else {
                console.warn('⚠️ Button has no aria-controls attribute!');
            }

            // ── Priority 3: Look in known card containers ──
            const card = ctaButtonOrId.closest('.button-price-area, .boost-sd__product-item, .card-wrapper');
            console.log('Closest known container:', card);
            if (card) {
                const localForm = card.querySelector('.notify-me-form');
                console.log('Local .notify-me-form inside container:', localForm);
                if (localForm) {
                    console.groupEnd();
                    return localForm;
                }
            }
        }

        // ── Fallback: global form ──
        const globalForm = document.querySelector('.notify-me-form--global') || document.querySelector('.notify-me-form');
        console.log('Falling back to global form:', globalForm);
        console.groupEnd();
        return globalForm;
    }

    /**
     * Get the notify-me-feedback element.
     * Looks for a feedback div near the provided element (for PLP/Product Cards),
     * otherwise falls back to the global feedback container.
     */
    function getSiblingFeedback(element) {
        if (element) {
            // 0a. NEW: card widget scope — button is in .notify-me-container, feedback is in
            //     .notify-me-form, both inside the same .notify-me-card-widget.
            //     Used for the "already subscribed" message shown when CTA button is clicked.
            const widget = element.closest('.notify-me-card-widget');
            if (widget) {
                const widgetFeedback = widget.querySelector('.notify-me-feedback');
                if (widgetFeedback) return widgetFeedback;
            }

            // 0b. Look for a feedback div INSIDE or immediately AFTER the closest open notify-me-form.
            //     This ensures errors appear correctly for both standard cards and Boost AI cards
            //     where the feedback element is positioned right after the form container.
            const form = element.closest('.notify-me-form');
            if (form) {
                // INSIDE
                const formFeedback = form.querySelector('.notify-me-feedback');
                if (formFeedback) {
                    // Check if feedback is trapped inside a notify-me-form. 
                    // If the form is closed, the feedback will be invisible (overflow: hidden).
                    // Move it to be a sibling of the form to ensure visibility.
                    if (formFeedback.parentNode === form.querySelector('.notify-me-form__inner')) {
                        form.insertAdjacentElement('afterend', formFeedback);
                    }
                    return formFeedback;
                }
                
                // AFTER as next sibling
                let next = form.nextElementSibling;
                // Skips any empty text nodes or comments to find the next actual element sibling
                while (next && next.nodeType !== 1) next = next.nextSibling;
                
                if (next && next.classList.contains('notify-me-feedback')) {
                    return next;
                }
            }

            // 1. Try finding a sibling with the class (PLP custom buttons)
            const parent = element.parentElement;
            if (parent) {
                const sibling = parent.querySelector('.notify-me-feedback');
                if (sibling) return sibling;
            }

            // 2. Try looking within the closest container (PDP / Standard structure / Boost SD)
            const container = element.closest('.notify-me-container') || 
                              element.closest('.product-form') || 
                              element.closest('.boost-sd__product-item');
            if (container) {
                const feedback = container.querySelector('.notify-me-feedback');
                if (feedback) {
                    // Check if feedback is trapped inside a notify-me-form. 
                    // If the form is closed, the feedback will be invisible (overflow: hidden).
                    // Move it to be a sibling of the form to ensure visibility.
                    const parentForm = feedback.closest('.notify-me-form');
                    if (parentForm && feedback.parentNode === parentForm.querySelector('.notify-me-form__inner')) {
                        parentForm.insertAdjacentElement('afterend', feedback);
                    }
                    return feedback;
                }
            }
        }

        // 3. Fallback to global feedback
        return document.querySelector('.notify-me-feedback--global') || document.querySelector('.notify-me-feedback');
    }

    /**
     * Open the inline form with slide animation
     */
    function openForm(form, ctaButton, container) {
        // Update form inputs with latest customer data from container
        syncFormFields(form, container);
        syncSubmitState(form);

        // PLP / Global form: copy product data from the clicked CTA button into the
        // global submit button so handleFormSubmit knows which product to register.
        // On product page this is never needed (form is variant-specific).
        if (form.classList.contains('notify-me-form--global') && ctaButton && ctaButton.dataset) {
            const submitBtn = form.querySelector('.notify-me-submit-btn');
            if (submitBtn) {
                submitBtn.dataset.productId = ctaButton.dataset.productId || '';
                submitBtn.dataset.variantId = ctaButton.dataset.variantId || '';
                submitBtn.dataset.sku = ctaButton.dataset.sku || '';
                console.log('[NotifyMe] Global form submit btn synced from PLP button:', {
                    productId: submitBtn.dataset.productId,
                    variantId: submitBtn.dataset.variantId,
                    sku: submitBtn.dataset.sku
                });
            }
        }

        form.classList.add('is-open');
        form.setAttribute('aria-hidden', 'false');
        if (ctaButton && typeof ctaButton.setAttribute === 'function') {
            ctaButton.setAttribute('aria-expanded', 'true');
        }

        // If the form is still inside an anchor (legacy layout), block the anchor navigation.
        // NOTE: form.closest('a') returns null when form is outside the anchor, so this is safe.
        const parentAnchor = form.closest('a');
        if (parentAnchor && !parentAnchor._notifyMeBlocker) {
            parentAnchor._notifyMeBlocker = (e) => {
                e.preventDefault();
            };
            parentAnchor.addEventListener('click', parentAnchor._notifyMeBlocker, true);
            console.log('[NotifyMe] Parent anchor blocked from navigation');
        }

        // Enable tabbing into form inputs
        const inputs = form.querySelectorAll('input');
        inputs.forEach(input => input.setAttribute('tabindex', '0'));

        // Scroll the form into view so the user can see it (especially useful on mobile)
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });

        console.log('[NotifyMe] Form opened');
    }

    /**
     * Close the inline form with slide animation
     */
    function closeForm(form, ctaButton) {
        form.classList.remove('is-open');
        form.classList.remove('is-validated');
        form.setAttribute('aria-hidden', 'true');

        form.querySelectorAll('.form-control').forEach((input) => {
            input.classList.remove('is-touched');
            clearFieldError(input);
        });

        if (ctaButton) {
            ctaButton.setAttribute('aria-expanded', 'false');
        }

        // Restore the parent anchor's click navigation now that the form is closed
        const parentAnchor = form.closest('a');
        if (parentAnchor && parentAnchor._notifyMeBlocker) {
            parentAnchor.removeEventListener('click', parentAnchor._notifyMeBlocker, true);
            delete parentAnchor._notifyMeBlocker;
            console.log('[NotifyMe] Parent anchor navigation restored');
        }

        // Disable tabbing into hidden form inputs
        const inputs = form.querySelectorAll('input');
        inputs.forEach(input => input.setAttribute('tabindex', '-1'));

        console.log('[NotifyMe] Form closed');
    }

    /**
     * Get or generate a Guest Session ID
     */
    function getGuestSessionId() {
        let sessionId = localStorage.getItem(STORAGE_KEYS.GUEST_SESSION_ID);
        if (!sessionId) {
            sessionId = 'guest_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
            localStorage.setItem(STORAGE_KEYS.GUEST_SESSION_ID, sessionId);
        }
        return sessionId;
    }

    function getStoredGuestVariantIds() {
        try {
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.GUEST_VARIANT_IDS) || '[]');
            return Array.isArray(stored) ? stored.map(String).filter(Boolean) : [];
        } catch (error) {
            console.warn('[NotifyMe] Failed to read guest variant cache:', error);
            return [];
        }
    }

    function storeGuestVariantId(variantId) {
        if (!variantId) return;

        try {
            const stored = getStoredGuestVariantIds();
            const next = Array.from(new Set([...stored, String(variantId)]));
            localStorage.setItem(STORAGE_KEYS.GUEST_VARIANT_IDS, JSON.stringify(next));
            console.log('[NotifyMe] Guest variant cache updated:', next);
        } catch (error) {
            console.warn('[NotifyMe] Failed to update guest variant cache:', error);
        }
    }

    /**
     * Sync form input values from container data attributes OR localStorage (for guests)
     */
    function syncFormFields(form, container) {
        const nameInput = form.querySelector('input[type="text"]');
        const emailInput = form.querySelector('input[type="email"]');
        const phoneInput = form.querySelector('input[type="tel"]');

        const isGuest = form.dataset.isGuest === 'true';

        if (isGuest) {
            // Guest: Try to pre-fill from localStorage
            const savedData = JSON.parse(localStorage.getItem(STORAGE_KEYS.GUEST_DATA) || '{}');

            if (nameInput) {
                nameInput.value = savedData.name || '';
                nameInput.readOnly = false;
                nameInput.removeAttribute('tabindex');
            }
            if (emailInput) {
                emailInput.value = savedData.email || '';
                emailInput.readOnly = false;
                emailInput.removeAttribute('tabindex');
            }
            if (phoneInput) {
                phoneInput.value = savedData.phone || '';
                phoneInput.readOnly = false;
                phoneInput.removeAttribute('tabindex');
            }
        } else {
            // Logged in: Sync fresh from container data OR global window object
            if (container) {
                console.log('[NotifyMe] syncFormFields — using container dataset', container.dataset);
            } else {
                console.log('[NotifyMe] syncFormFields — no container found, falling back to window.__ZYDUS_CUSTOMER__', window.__ZYDUS_CUSTOMER__);
            }
            const source = container ? container.dataset : (window.__ZYDUS_CUSTOMER__ ? {
                customerName: window.__ZYDUS_CUSTOMER__.name,
                customerEmail: window.__ZYDUS_CUSTOMER__.email,
                customerPhone: window.__ZYDUS_CUSTOMER__.phone
            } : {});

            if (nameInput) nameInput.value = source.customerName || '';
            if (emailInput) emailInput.value = source.customerEmail || '';
            if (phoneInput) phoneInput.value = source.customerPhone || '';
            console.log('[NotifyMe] syncFormFields — form pre-filled with:', { name: source.customerName, email: source.customerEmail, phone: source.customerPhone });
        }
        syncSubmitState(form);
    }

    /**
     * Handle direct subscription for logged-in users (bypasses form)
     */
    async function handleDirectSubmit(ctaBtn, container, form) {
        if (isProcessing) return;

        const productId = ctaBtn.dataset.productId;
        const variantId = ctaBtn.dataset.variantId;

        console.log('[NotifyMe] handleDirectSubmit — reading from button:', {
            element: ctaBtn.className,
            productId,
            variantId,
            sku: ctaBtn.dataset.sku
        });
        const sku = ctaBtn.dataset.sku;

        const name = container.dataset.customerName;
        const email = container.dataset.customerEmail;
        const phone = container.dataset.customerPhone;

        console.log('[NotifyMe] Direct submit', { productId, variantId, sku, name, email, phone });

        await processSubscription({
            productId,
            variantId,
            sku,
            name,
            email,
            phone,
            isGuest: false,
            customerId: form.dataset.customerId,
            submitBtn: ctaBtn, // Use CTA button for loading state
            form: form,
            container: container
        });
    }

    /**
     * Handle submit button click inside the form
     */
    async function handleFormSubmit(event) {
        event.preventDefault();
        event.stopPropagation();

        if (isProcessing) {
            console.log('[NotifyMe] Already processing, ignoring submit');
            return;
        }

        const submitBtn = event.target.closest('.notify-me-submit-btn');
        if (!submitBtn) return;
        const form = submitBtn.closest('.notify-me-form');
        if (!form) return;

        if (form.dataset.isGuest === 'true' || isGuestCustomer()) {
            console.log('[NotifyMe] Guest submit blocked - redirecting to login first');
            redirectGuestToLogin();
            return;
        }

        if (!validateNativeForm(form, submitBtn)) {
            return;
        }

        // Extract values DIRECTLY from inputs (so guest edits are captured)
        const nameInput = form.querySelector('input[type="text"]').value.trim();
        const emailInput = form.querySelector('input[type="email"]').value.trim();
        const phoneInput = form.querySelector('input[type="tel"]').value.trim();

        const productId = submitBtn.dataset.productId;
        const variantId = submitBtn.dataset.variantId;
        const sku = submitBtn.dataset.sku;
        const isGuest = form.dataset.isGuest === 'true';

        console.log('[NotifyMe] Form submitted', { productId, variantId, sku, email: emailInput, isGuest });

        await processSubscription({
            productId,
            variantId,
            sku,
            name: nameInput,
            email: emailInput,
            phone: phoneInput,
            isGuest,
            customerId: !isGuest ? form.dataset.customerId : getGuestSessionId(),
            submitBtn: submitBtn,
            form: form,
            container: null
        });
    }

    /**
     * Internal: Process the subscription (de-dup + API)
     */
    async function processSubscription(options) {
        const { productId, variantId, sku, name, email, phone, isGuest, customerId, submitBtn, form, container } = options;

        // Basic validation
        if (!name || !email || !phone) {
            showFeedback(submitBtn, 'Please fill in all fields (Name, Email, Phone).', 'error');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showFeedback(submitBtn, 'Please enter a valid email address.', 'error');
            return;
        }

        // Clean phone number for API
        const cleanedPhoneNo = cleanPhoneNumber(phone);
        if (!cleanedPhoneNo) {
            showFeedback(submitBtn, 'Please enter a valid 10-digit mobile number.', 'error');
            return;
        }

        setButtonLoading(submitBtn, true);
        isProcessing = true;

        try {
            if (isGuest) {
                // For guests: Save to localStorage for future auto-fill
                localStorage.setItem(STORAGE_KEYS.GUEST_DATA, JSON.stringify({
                    name,
                    email,
                    phone
                }));
            }

            // Step 1: De-duplicate check
            const alreadySubscribed = await checkDuplicate(cleanedPhoneNo, variantId, isGuest);

            if (alreadySubscribed) {
                console.log('[NotifyMe] Duplicate detected — already subscribed to this variant');
                if (form) closeForm(form, null);
                showFeedback(submitBtn, 'You are already subscribed! We will notify you when this item is back in stock.', 'info');
                return;
            }

            // Step 2: Subscribe
            await subscribeToNotification(productId, variantId, sku, cleanedPhoneNo, name, email, customerId, submitBtn, form, isGuest);

        } catch (error) {
            console.error('[NotifyMe] Error during subscription:', error);
            showFeedback(submitBtn, 'Something went wrong. Please try again.', 'error');
        } finally {
            isProcessing = false;
            setButtonLoading(submitBtn, false);
        }

        syncSubmitState(form);
    }

    function getNotifyInputs(form) {
        return Array.from(form.querySelectorAll('.form-control'));
    }

    function getFirstInvalidInput(form) {
        return getNotifyInputs(form).find((input) => !input.checkValidity()) || null;
    }

    function syncSubmitState(form) {
        if (!form) return;

        const hasValue = getNotifyInputs(form).some((input) => input.value.trim() !== '');
        form.classList.toggle('is-validated', hasValue);
    }

    function syncAllSubmitStates() {
        document.querySelectorAll('.notify-me-form').forEach((form) => {
            syncSubmitState(form);
        });
    }

    function validateNativeForm(form, feedbackContext) {
        if (!form) return false;

        form.classList.add('is-validated');
        getNotifyInputs(form).forEach((input) => {
            input.classList.add('is-touched');
            syncFieldValidationState(input);
        });
        syncSubmitState(form);

        const invalidInput = getFirstInvalidInput(form);
        if (!invalidInput) {
            return true;
        }

        if (feedbackContext) {
            showFeedback(feedbackContext, invalidInput.validationMessage, 'error');
        }

        invalidInput.focus();
        return false;
    }

    function getNormalizedVariantId(item) {
        if (!item || typeof item !== 'object') return '';

        return String(
            item.variantid
            || item.variant_id
            || item.variantId
            || item.shopify_variant_id
            || item.shopifyVariantId
            || ''
        );
    }

    function getFieldErrorElement(input) {
        if (!input) return null;

        const formField = input.closest('.form-field');
        if (!formField) return null;

        let errorEl = formField.querySelector('.notify-me-field-error');
        if (!errorEl) {
            errorEl = document.createElement('div');
            errorEl.className = 'notify-me-field-error';
            formField.appendChild(errorEl);
        }

        return errorEl;
    }

    function clearFieldError(input) {
        const errorEl = getFieldErrorElement(input);
        if (!errorEl) return;

        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
    }

    function syncFieldValidationState(input) {
        if (!input) return;

        const errorEl = getFieldErrorElement(input);
        if (!errorEl) return;

        if (input.checkValidity()) {
            clearFieldError(input);
            return;
        }

        errorEl.textContent = input.validationMessage;
        errorEl.classList.add('is-visible');
    }

    /**
     * Check if user is already subscribed to this variant (de-dup)
     * Returns true if already subscribed, false otherwise
     */
    async function checkDuplicate(cleanedPhoneNo, variantId, isGuest) {
        console.log('[NotifyMe] Checking for duplicate subscription...');

        const endpoint = isGuest ? CONFIG.GET_NOTIFIED_PRODUCTS_URL_GUEST : CONFIG.GET_NOTIFIED_PRODUCTS_URL;

        try {
            const response = await fetch(
                `${endpoint}?phone_no=${cleanedPhoneNo}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            const responseText = await response.text();

            let result;
            try {
                const jsonResponse = JSON.parse(responseText);
                // JSONBin wraps data in 'record' property
                result = jsonResponse.record || jsonResponse;
            } catch {
                console.warn('[NotifyMe] Could not parse de-dup response, allowing subscription to proceed');
                return false;
            }

            if (response.ok && result.success && Array.isArray(result.data)) {
                const match = result.data.find(item => getNormalizedVariantId(item) === String(variantId));
                console.log('[NotifyMe] De-dup check result:', match ? 'DUPLICATE' : 'NEW');
                return !!match;
            }

            return false;

        } catch (error) {
            console.warn('[NotifyMe] De-dup check failed, allowing subscription to proceed:', error);
            return false; // Silently allow on network error
        }
    }

    async function fetchVariantIdsByPhone(phone, endpoint, sourceLabel) {
        const cleanedPhone = cleanPhoneNumber(phone);
        if (!cleanedPhone) {
            console.log(`[NotifyMe] Skipping ${sourceLabel} subscription fetch - invalid phone`);
            return [];
        }

        console.log(`[NotifyMe] Fetching ${sourceLabel} subscriptions for:`, cleanedPhone);

        try {
            const response = await fetch(`${endpoint}?phone_no=${cleanedPhone}`);
            const data = await response.json();
            const result = data.record || data;

            if (response.ok && result.success && Array.isArray(result.data)) {
                const variantIds = result.data
                    .map(item => getNormalizedVariantId(item))
                    .filter(Boolean);

                console.log(`[NotifyMe] ${sourceLabel} subscriptions loaded:`, variantIds);
                return variantIds;
            }
        } catch (error) {
            console.warn(`[NotifyMe] Failed to fetch ${sourceLabel} subscriptions:`, error);
        }

        return [];
    }

    /**
     * Fetch all variant IDs the current user is already subscribed to.
     * Stores them in subbedVariantIds variable.
     */
    async function fetchSubscribedProducts() {
        const phoneInput = document.getElementById('customer_phone');
        let phone = phoneInput ? phoneInput.value : '';

        const isGuest = document.querySelector('.notify-me-form--global')?.dataset.isGuest === 'true'
            || document.querySelector('.notify-me-form')?.dataset.isGuest === 'true';

        if (isGuest) {
            subbedVariantIds = [];
            console.log('[NotifyMe] Guest subscription fetch skipped - login required for Notify Me');
            return;
        }

        const savedData = JSON.parse(localStorage.getItem(STORAGE_KEYS.GUEST_DATA) || '{}');
        const guestPhone = savedData.phone || '';
        const guestStoredVariantIds = getStoredGuestVariantIds();

        if ((!phone || phone === '0') && isGuest) {
            phone = guestPhone;
        }

        if ((!phone || phone === '0') && !isGuest && window.__ZYDUS_CUSTOMER__) {
            phone = window.__ZYDUS_CUSTOMER__.phone || '';
        }

        const requests = [];
        const sourceVariantIds = [];

        if (phone) {
            const primaryEndpoint = isGuest ? CONFIG.GET_NOTIFIED_PRODUCTS_URL_GUEST : CONFIG.GET_NOTIFIED_PRODUCTS_URL;
            requests.push(
                fetchVariantIdsByPhone(phone, primaryEndpoint, isGuest ? 'guest' : 'customer')
            );
        }

        // In guest mode, also check the customer notify list for the same mobile number.
        // This lets a logged-in subscription appear as "Notified" after logout when the
        // guest profile is using the same phone number.
        if (isGuest && guestPhone) {
            requests.push(
                fetchVariantIdsByPhone(guestPhone, CONFIG.GET_NOTIFIED_PRODUCTS_URL, 'customer-by-guest-phone')
            );
        }

        // If the user subscribed before login, also load guest subscriptions from this browser
        // and merge them into the logged-in UI state.
        if (!isGuest && guestPhone) {
            requests.push(
                fetchVariantIdsByPhone(guestPhone, CONFIG.GET_NOTIFIED_PRODUCTS_URL_GUEST, 'guest-merged')
            );
        }

        if (requests.length === 0) return;

        try {
            const results = await Promise.all(requests);
            results.forEach((variantIds) => {
                sourceVariantIds.push(...variantIds);
            });

            if (isGuest || guestStoredVariantIds.length > 0) {
                sourceVariantIds.push(...guestStoredVariantIds);
            }

            subbedVariantIds = Array.from(new Set(sourceVariantIds));
            console.log('[NotifyMe] Initial subscriptions merged:', subbedVariantIds);

            // Mark existing buttons on page
            markSubscribedButtons();
        } catch (error) {
            console.warn('[NotifyMe] Failed to merge initial subscriptions:', error);
        }
    }

    /**
     * Update the text inside a button's text container (span or the button itself)
     */
    function updateButtonText(button, text) {
        if (!button) return;
        const textSpan = button.querySelector('.notify-me-text, .notify-me-submit-text');
        if (textSpan) {
            textSpan.textContent = text;
        } else {
            // Find just the text node or fallback to textContent
            // PLP buttons often don't have the span wrapper
            button.textContent = text;
        }
    }

    /**
     * Apply the visual subscribed state to a Notify Me CTA.
     * Keep the button clickable so we can still show the "already subscribed" feedback.
     */
    function syncSubscribedStateForButton(button) {
        if (!button) return false;

        const variantId = String(button.dataset.variantId || '');
        const isSubscribed = !!variantId && subbedVariantIds.includes(variantId);
        const wasSubscribed = button.classList.contains('subscribed');

        if (isSubscribed) {
            if (!wasSubscribed) {
                button.classList.add('subscribed');
            }
            if (button.dataset.notifyLabel !== 'Notified') {
                updateButtonText(button, 'Notified');
                button.dataset.notifyLabel = 'Notified';
            }
        } else {
            if (wasSubscribed) {
                button.classList.remove('subscribed');
            }
            // Do not rewrite every non-subscribed button on the page.
            // That was causing repeated observer churn on collection/home pages.
            delete button.dataset.notifyLabel;
        }

        return isSubscribed;
    }

    /**
     * Scan the page for Notify Me buttons and mark them as "Already Subscribed" 
     * if their variantId matches the subbedVariantIds list.
     *
     * Queries BOTH .notify-me-btn (standard) and .plp-nofify-me-btn (PLP custom) to
     * handle the case where PLP custom buttons haven't yet had .notify-me-btn injected
     * (e.g. called from pagination observer before injectPhoneToNotifyButtons runs).
     */
    function markSubscribedButtons() {
        // Use a Set to avoid processing the same element twice
        // (for buttons that already have both classes)
        const seen = new Set();
        const allButtons = document.querySelectorAll('.notify-me-btn, .plp-nofify-me-btn');
        allButtons.forEach(btn => {
            if (seen.has(btn)) return;
            seen.add(btn);

            const isSubscribed = syncSubscribedStateForButton(btn);
            if (isSubscribed && !btn.dataset.subscribedLogged) {
                console.log('[NotifyMe] Marking button as already subscribed:', btn.dataset.variantId);
                btn.dataset.subscribedLogged = 'true';
            } else if (!isSubscribed && btn.dataset.subscribedLogged) {
                delete btn.dataset.subscribedLogged;
            }
        });
    }

    /**
     * Subscribe to back-in-stock notification via API
     */
    async function subscribeToNotification(productId, variantId, sku, phoneNo, name, email, customerId, submitBtn, form, isGuest) {
        const payload = {
            variant_id: parseInt(variantId),
            product_id: parseInt(productId),
            sku: sku,
            phone_no: phoneNo,
            name: name,
            email: email,
            customer_id: customerId // this will be either the Shopify ID or the Guest Session ID
        };

        const endpoint = isGuest ? CONFIG.API_URL_GUEST : CONFIG.API_URL;
        console.log('[NotifyMe] Calling API with payload:', payload, 'to endpoint:', endpoint);

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        console.log('[NotifyMe] API response:', data);

            if (response.ok) {
                // Close form
                closeForm(form, null);

            // Cache the newly subscribed variantId so pagination/new-card checks work
            // without needing another API round-trip
            if (variantId && !subbedVariantIds.includes(String(variantId))) {
                subbedVariantIds.push(String(variantId));
                console.log('[NotifyMe] Added variantId to local subbedVariantIds cache:', variantId);
            }

            if (isGuest) {
                storeGuestVariantId(variantId);
            }

            // Find the PLP card button (sibling of the form in .boost-sd__product-item or .button-price-area)
            const card = form.closest('.boost-sd__product-item, .button-price-area, .card-wrapper');
            const plpBtn = card ? card.querySelector('.plp-nofify-me-btn') : null;

            if (plpBtn) {
                // Mark the card's Notify Me button as subscribed
                plpBtn.classList.add('subscribed');
                updateButtonText(plpBtn, 'Notified');

                // Show the success feedback next to the card button
                showFeedback(plpBtn, 'You will be notified when this item is back in stock!', 'success');
            } else {
                // Fallback: show next to the submit button (PDP or non-PLP flow)
                showFeedback(submitBtn, 'You will be notified when this item is back in stock!', 'success');
            }

            // Disable and mark the form submit button too
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.classList.add('subscribed');
            }

            markSubscribedButtons();

        } else {
            const errorMessage = data.message || 'Failed to subscribe. Please try again.';
            showFeedback(submitBtn, errorMessage, 'error');
        }
    }

    /**
     * Clean phone number — remove country code and formatting
     * API expects 10-digit number without country code
     */
    function cleanPhoneNumber(phoneNo) {
        if (!phoneNo) return null;

        let cleaned = String(phoneNo).trim();
        cleaned = cleaned.replace(/^\+?91[-\s]?/, ''); // Remove +91, 91, +91-
        cleaned = cleaned.replace(/[^\d]/g, '');       // Remove non-digits

        const result = cleaned.slice(-10);
        return result.length === 10 ? result : null;
    }

    /**
     * Set loading state on a button (CTA or Submit)
     */
    function setButtonLoading(button, loading) {
        if (!button) return;

        const textSpan = button.querySelector('.notify-me-text, .notify-me-submit-text');
        const loadingSpan = button.querySelector('.notify-me-loading, .notify-me-submit-loading');

        button.disabled = loading;
        
        if (textSpan) {
            textSpan.style.display = loading ? 'none' : 'inline-flex';
        }

        if (loadingSpan) {
            loadingSpan.style.display = loading ? 'inline-flex' : 'none';
        }
    }

    /**
     * Show feedback message below the CTA button
     */
    function showFeedback(contextElement, message, type) {
        // Find the feedback div near the element that triggered the action
        const feedbackEl = getSiblingFeedback(contextElement);
        if (!feedbackEl) {
            console.warn('[NotifyMe] showFeedback — No feedback element found for:', contextElement);
            return;
        }

        // Clear any existing auto-hide timeout for this element
        if (feedbackEl._hideTimeout) {
            clearTimeout(feedbackEl._hideTimeout);
        }

        feedbackEl.textContent = message;
        feedbackEl.className = `notify-me-feedback ${type}`;
        
        // Ensure visibility
        feedbackEl.style.display = 'block';
        
        console.log(`[NotifyMe] showFeedback — message displayed in ${feedbackEl.className}:`, message);

        // Auto-hide after 5 seconds
        feedbackEl._hideTimeout = setTimeout(() => {
            feedbackEl.style.display = 'none';
        }, CONFIG.FEEDBACK_DURATION);
    }

    /**
     * Hide feedback message and reset form to initial state
     */
    function resetNotifyMeForm(container) {
        // container argument is kept for compatibility but we operate globally
        document.querySelectorAll('.notify-me-form.is-open').forEach((form) => {
            closeForm(form, null);
        });

        document.querySelectorAll('.notify-me-feedback').forEach((feedback) => {
            feedback.style.display = 'none';
            feedback.textContent = '';
            feedback.className = 'notify-me-feedback';
        });

        // Re-enable CTA buttons in case they were only disabled for loading state
        document.querySelectorAll('.notify-me-btn').forEach(btn => {
            setButtonLoading(btn, false);
            btn.disabled = false;
        });
    }

    /**
     * Observe variant changes to update button visibility + reset form.
     *
     * KEY INSIGHT (from variant-selects.js):
     * The theme's toggleAddButton() sets/removes `disabled` on the main ATC button.
     * We MutationObserve THAT attribute — so we react the instant the theme updates it,
     * with zero race conditions. No setTimeout needed.
     */
    function observeVariantChanges() {
        const atcButton = getMainATCButton();

        if (atcButton) {
            // Primary signal: watch the ATC button's `disabled` attribute directly
            const atcObserver = new MutationObserver(() => {
                console.log('[NotifyMe] ATC button disabled attribute changed');
                handleVariantChange();
            });

            atcObserver.observe(atcButton, {
                attributes: true,
                attributeFilter: ['disabled']
            });

            console.log('[NotifyMe] Observing ATC button disabled state');
        } else {
            console.warn('[NotifyMe] ATC button not found — falling back to event listener');
        }

        // Fallback: theme's variant:changed event (fires after Section Rendering fetch)
        document.addEventListener('variant:changed', handleVariantChange);

        // Also listen for clicks on custom variant selector buttons (with small delay
        // to let theme JS update the ATC button state first)
        attachCustomVariantListeners();

        // Observe class changes on custom variant buttons
        observeCustomVariantButtons();
    }

    /**
     * Attach click listeners to custom variant selector buttons
     */
    function attachCustomVariantListeners() {
        const customButtons = document.querySelectorAll('.custom_link, .flavour_name, .family_color_option, .weight_name, .packaging_name');

        customButtons.forEach(button => {
            button.addEventListener('click', function () {
                console.log('[NotifyMe] Custom variant button clicked');
                setTimeout(() => {
                    updateButtonVisibility();
                }, 100);
            });
        });

        console.log(`[NotifyMe] Attached listeners to ${customButtons.length} custom variant button(s)`);
    }

    /**
     * Observe custom variant buttons for class changes (activeData, soldout)
     */
    function observeCustomVariantButtons() {
        const customButtons = document.querySelectorAll('.custom_link, .flavour_name, .family_color_option, .weight_name, .packaging_name');

        customButtons.forEach(button => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        console.log('[NotifyMe] Custom variant button class changed');
                        setTimeout(() => {
                            updateButtonVisibility();
                        }, 100);
                    }
                });
            });

            observer.observe(button, {
                attributes: true,
                attributeFilter: ['class']
            });
        });

        console.log(`[NotifyMe] Observing ${customButtons.length} custom variant button(s) for class changes`);
    }

    /**
     * Browser back/forward navigation can restore the old collection DOM from bfcache,
     * which means card buttons come back with stale text until the page is reloaded.
     * Re-fetch subscriptions on pageshow so "Notified" is restored without a reload.
     */
    function attachPageShowListener() {
        if (window.__zydusNotifyMePageShowBound) return;
        window.__zydusNotifyMePageShowBound = true;

        window.addEventListener('pageshow', (event) => {
            console.log('[NotifyMe] pageshow detected - re-syncing subscriptions', { persisted: event.persisted });

            // Back/forward restores and some collection re-renders can bring the page back
            // before Boost/custom cards are fully present. Re-fetch once, then retry marking
            // for a short window so restored cards get the "Notified" state without reload.
            fetchSubscribedProducts();

            let attempts = 0;
            const maxAttempts = 8;
            const intervalMs = 400;

            const poller = setInterval(() => {
                attempts += 1;

                markSubscribedButtons();
                injectPhoneToNotifyButtons();

                const pendingButtons = document.querySelectorAll('.notify-me-btn, .plp-nofify-me-btn');
                const subscribedButtons = document.querySelectorAll('.notify-me-btn.subscribed, .plp-nofify-me-btn.subscribed');

                if (attempts >= maxAttempts || pendingButtons.length === 0 || subscribedButtons.length > 0) {
                    clearInterval(poller);
                    console.log('[NotifyMe] pageshow re-sync finished', {
                        attempts,
                        pendingButtons: pendingButtons.length,
                        subscribedButtons: subscribedButtons.length
                    });
                }
            }, intervalMs);
        });
    }

    /**
     * Handle variant change — reset form and update data attributes
     * Visibility update happens via the ATC MutationObserver OR this handler
     */
    function handleVariantChange() {
        console.log('[NotifyMe] Variant changed');

        // Get new variant ID from the hidden variant input in the main form
        const variantInput = document.querySelector('input.product-variant-id');
        const variantId = variantInput ? variantInput.value : null;
        const containers = document.querySelectorAll('.notify-me-container:not(.boost-processed)');
        const submitButtons = document.querySelectorAll('.notify-me-form:not(.notify-me-form--global) .notify-me-submit-btn');
        const ctaButtons = document.querySelectorAll('.notify-me-container .notify-me-btn');

        // Update variant ID and SKU on both buttons so the form sends the correct data
        if (variantId) {
            ctaButtons.forEach((ctaBtn) => {
                ctaBtn.dataset.variantId = variantId;
            });
            submitButtons.forEach((submitBtn) => {
                submitBtn.dataset.variantId = variantId;
            });
        }

        const skuInput = document.querySelector('.product-sku');
        if (skuInput) {
            ctaButtons.forEach((ctaBtn) => {
                ctaBtn.dataset.sku = skuInput.value;
            });
            submitButtons.forEach((submitBtn) => {
                submitBtn.dataset.sku = skuInput.value;
            });
        }

        // Also update the container data-variant-id so getSiblingForm lookups stay fresh
        if (variantId) {
            containers.forEach((container) => {
                container.dataset.variantId = variantId;
            });
        }

        // Reset inline form (close + clear feedback)
        resetNotifyMeForm();

        // Update visibility based on ATC button state
        updateButtonVisibility();

        // Re-apply subscribed state for the newly selected variant on PDP CTAs
        markSubscribedButtons();
    }

    /**
     * Get the main ATC button — always a <button> with class main-btn.
     * The sticky bar uses a <p> tag with class sticky-btn, so this selector
     * is unambiguous and will never match the sticky element.
     */
    function getMainATCButton() {
        return document.querySelector('button.product-form__submit.main-btn:not(.notify-me-btn)');
    }

    /**
     * When Notify Me is the active CTA, purchase-only controls should not remain usable.
     * Keep this reversible so available variants/products restore their original state.
     */
    function setNotifyMePurchaseControlsState(isNotifyMeActive) {
        document.querySelectorAll('.product-form').forEach((formWrapper) => {
            formWrapper.querySelectorAll('.product-form__quantity').forEach((quantityWrapper) => {
                quantityWrapper.style.display = isNotifyMeActive ? 'none' : '';

                quantityWrapper.querySelectorAll('input, button, select').forEach((control) => {
                    if (isNotifyMeActive) {
                        if (!control.disabled) {
                            control.dataset.notifyMeWasEnabled = 'true';
                        }
                        control.disabled = true;
                        control.setAttribute('aria-disabled', 'true');
                    } else if (control.dataset.notifyMeWasEnabled === 'true') {
                        control.disabled = false;
                        control.removeAttribute('aria-disabled');
                        delete control.dataset.notifyMeWasEnabled;
                    }
                });
            });

            formWrapper.querySelectorAll('.shopify-payment-button').forEach((paymentButtonWrapper) => {
                paymentButtonWrapper.style.display = isNotifyMeActive ? 'none' : '';

                paymentButtonWrapper.querySelectorAll('button, input').forEach((control) => {
                    if (isNotifyMeActive) {
                        if (!control.disabled) {
                            control.dataset.notifyMeWasEnabled = 'true';
                        }
                        control.disabled = true;
                        control.setAttribute('aria-disabled', 'true');
                    } else if (control.dataset.notifyMeWasEnabled === 'true') {
                        control.disabled = false;
                        control.removeAttribute('aria-disabled');
                        delete control.dataset.notifyMeWasEnabled;
                    }
                });
            });
        });
    }

    function getQuantitySoldOutState() {
        const pdpQty = document.querySelector('body .pdp-qty');
        if (!pdpQty) return false;

        const max = Number(pdpQty.getAttribute('max') || pdpQty.getAttribute('data-max'));
        const availableQty = Number(pdpQty.getAttribute('data-available-qty'));
        const minQty = Number(pdpQty.getAttribute('min') || pdpQty.getAttribute('data-min') || 1);

        if (!Number.isNaN(max) && max <= 0) return true;
        if (!Number.isNaN(availableQty) && availableQty < minQty) return true;

        return false;
    }

    /**
     * Set initial button visibility on page load
     */
    function setInitialButtonVisibility() {
        console.log('[NotifyMe] Setting initial button visibility');
        updateButtonVisibility();
    }

    /**
     * Update button visibility.
     *
     * Source of truth: the main ATC button's `disabled` attribute.
     * — Liquid sets this correctly on page load.
     * — toggleAddButton() in variant-selects.js sets/removes it on variant change.
     * This is the SAME value the theme itself uses — no stale reads, no race conditions.
     */
    function updateButtonVisibility() {
        console.log('[NotifyMe] Updating button visibility');

        const notifyMeContainers = document.querySelectorAll('.notify-me-container:not(.boost-processed)');
        if (notifyMeContainers.length === 0) {
            console.log('[NotifyMe] .notify-me-container not found — skipping');
            return;
        }

        const addToCartButton = getMainATCButton();
        const stickyATCButton = document.querySelector('.sticky_addToCart .sticky-btn');

        if (!addToCartButton) {
            console.log('[NotifyMe] ATC button not found — skipping');
            return;
        }

        const eligibleNotifyMeContainers = Array.from(notifyMeContainers).filter(container => container.dataset.notifyMeEligible === 'true');
        const hasNotifyMeEligibleProduct = eligibleNotifyMeContainers.length > 0;
        const hasUnavailableNotifyMeContainer = eligibleNotifyMeContainers.some(container => container.dataset.available === 'false');
        const isSoldOut = addToCartButton.hasAttribute('disabled') || hasUnavailableNotifyMeContainer || getQuantitySoldOutState();

        // Remove ALL initial loaders since we now know which button to display
        document.querySelectorAll('.notify-me-initial-loader').forEach(loader => {
            loader.style.display = 'none';
        });

        if (isSoldOut && hasNotifyMeEligibleProduct) {
            notifyMeContainers.forEach(container => container.style.display = 'none');
            eligibleNotifyMeContainers.forEach(container => container.style.display = 'block');
            addToCartButton.style.display = 'none';
            if (stickyATCButton) stickyATCButton.style.display = 'none';
            setNotifyMePurchaseControlsState(true);
            console.log('[NotifyMe] ❌ ATC disabled + notify-me tag — showing Notify Me');
        } else if (isSoldOut) {
            notifyMeContainers.forEach(container => container.style.display = 'none');
            addToCartButton.style.display = 'flex';
            if (stickyATCButton) stickyATCButton.style.display = 'flex';
            setNotifyMePurchaseControlsState(false);
            console.log('[NotifyMe] ❌ ATC disabled without notify-me tag — showing Sold Out');
        } else {
            notifyMeContainers.forEach(container => container.style.display = 'none');
            addToCartButton.style.display = 'flex';
            if (stickyATCButton) stickyATCButton.style.display = 'flex';
            setNotifyMePurchaseControlsState(false);
            console.log('[NotifyMe] ✅ ATC enabled — showing Add to Cart globally');
        }
    }

    // --- BOOST SD INTEGRATION ---
    function initBoostSDObserver() {
        const targetNode = document.body;
        const config = { childList: true, subtree: true };

        const callback = (mutationsList, observer) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(node => {
                        if (node.nodeType === 1) { // ELEMENT_NODE
                            if (node.classList && node.classList.contains('boost-sd__product-item')) {
                                processBoostSDProduct(node);
                            } else if (node.querySelectorAll) {
                                node.querySelectorAll('.boost-sd__product-item').forEach(processBoostSDProduct);
                            }
                        }
                    });
                    // After processing new cards, mark any already-subscribed buttons
                    // (covers guest users whose subbedVariantIds is populated from localStorage phone)
                    if (subbedVariantIds.length > 0) {
                        markSubscribedButtons();
                    }
                }
            }
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);

        // Process any existing items on load
        document.querySelectorAll('.boost-sd__product-item').forEach(processBoostSDProduct);
    }

    function processBoostSDProduct(productItem) {
        const existingPdpQty = productItem.querySelector('.pdp-qty');
        const hasKnownNotifyMeEligibility = !!existingPdpQty;
        const isNotifyMeEligible = existingPdpQty ? existingPdpQty.getAttribute('data-notify-me-eligible') === 'true' : false;
        const notifyButtons = productItem.querySelectorAll('button[disabled].plp-add-btn');
        notifyButtons.forEach(btn => {
            if (btn.innerText.trim() === 'Notify Me' && btn.dataset.notifyJsProcessed !== 'true' && isNotifyMeEligible) {
                btn.dataset.notifyJsProcessed = 'true';
                btn.classList.add('notify-me-btn');
                if (window.__ZYDUS_CUSTOMER__) {
                    btn.setAttribute('data-phone-no', window.__ZYDUS_CUSTOMER__.phone || '');
                }
                btn.removeAttribute('disabled');

                // Check if already subscribed
                if (subbedVariantIds.includes(String(btn.dataset.variantId))) {
                    btn.classList.add('subscribed');
                    if (btn.classList.contains('plp-nofify-me-btn')) {
                        updateButtonText(btn, 'Notified');
                    }
                    // Feedback handled on click in handleCTAClick
                }
            } else if (btn.innerText.trim() === 'Notify Me' && hasKnownNotifyMeEligibility && !isNotifyMeEligible) {
                btn.innerText = 'Sold Out';
                btn.classList.remove('notify-me-btn');
                btn.dataset.notifyJsProcessed = 'true';
            }
        });

        const button = productItem.querySelector('.boost-sd__button--add-to-cart');
        const mainQty = productItem.querySelector('.main-qtyy');

        if (!button || button.dataset.notifyJsProcessed === 'true') return;

        // Set processed flag to avoid infinite loops
        button.dataset.notifyJsProcessed = 'true';

        if (mainQty) {
            const url = mainQty.getAttribute('data-url');
            if (url && !mainQty.querySelector('.pdp-qty')) {
                // Fetch pdp-qty HTML if not present
                fetch(url)
                    .then(res => res.ok ? res.text() : Promise.reject(res))
                    .then(data => {
                        mainQty.innerHTML = data;
                        evaluateBoostSDProduct(productItem, button, mainQty);
                    })
                    .catch(console.error);
            } else {
                evaluateBoostSDProduct(productItem, button, mainQty);
            }
        }
    }

    function evaluateBoostSDProduct(productItem, button, mainQty) {
        const pdpQty = mainQty.querySelector('.pdp-qty');
        if (!pdpQty) return;

        const prdId = pdpQty.getAttribute('data-product-id');
        const variantId = prdId;
        const sku = pdpQty.getAttribute('data-sku');
        const maxLimit = Number(pdpQty.getAttribute('data-max-cart'));
        const isNotifyMeEligible = pdpQty.getAttribute('data-notify-me-eligible') === 'true';

        if (maxLimit === 0 && isNotifyMeEligible) {
            button.innerText = 'Notify Me';
            button.classList.add('notify-me-btn');
            button.classList.remove('boost-sd__button--add-to-cart');

            button.setAttribute('data-product-id', prdId);
            button.setAttribute('data-variant-id', variantId);
            button.setAttribute('data-sku', sku);

            if (window.__ZYDUS_CUSTOMER__) {
                button.setAttribute('data-phone-no', window.__ZYDUS_CUSTOMER__.phone || '');
            }

            button.removeAttribute('onclick');
            button.removeAttribute('disabled');
        } else if (maxLimit === 0) {
            button.innerText = 'Sold Out';
            button.classList.remove('notify-me-btn');
            button.classList.add('boost-sd__button--add-to-cart');
            button.removeAttribute('onclick');
            button.setAttribute('disabled', 'disabled');
        }
    }

    /**
     * Scan ALL .plp-add-btn "Notify Me" buttons on the page and inject customer phone.
     * Reads phone from #customer_phone hidden input (set in theme.liquid).
     * Retries every 300ms to catch buttons rendered late by Boost SD.
     */
    function injectPhoneToNotifyButtons() {
        // Read phone from the hidden input injected by theme.liquid
        const phoneInput = document.getElementById('customer_phone');
        const phone = phoneInput ? phoneInput.value : '';

        if (!phone || phone === '0') {
            console.log('[NotifyMe] injectPhoneToNotifyButtons — #customer_phone is empty or 0 (guest), skipping injection');
            return;
        }

        console.log(`[NotifyMe] injectPhoneToNotifyButtons — phone="${phone}", scanning for PLP Notify Me buttons`);

        // Two types of Notify Me buttons on PLP:
        //  1. Boost SD generated: .boost-sd__button.plp-add-btn.boost-sd__button--select-option
        //  2. Custom button added in card template: .plp-nofify-me-btn
        const allNotifyButtons = document.querySelectorAll(
            'button.plp-nofify-me-btn'
        );
        console.log(`[NotifyMe] injectPhoneToNotifyButtons — found ${allNotifyButtons.length} button(s)`);

        allNotifyButtons.forEach(btn => {
            // Use textContent (not innerText) — innerText returns "" for hidden/off-screen elements
            // which Boost SD does during initial render, causing the text check to always fail
            const text = (btn.textContent || '').trim();
            const alreadyDone = btn.dataset.phoneInjected === 'true';

            console.log(`[NotifyMe]   → actual text="${text}", phoneInjected=${alreadyDone}, variantId=${btn.dataset.variantId}`);

            if (text === 'Notify Me' && !alreadyDone) {
                btn.setAttribute('data-phone-no', phone);
                btn.dataset.phoneInjected = 'true';

                // Always add .notify-me-btn — do NOT make this conditional on disabled.
                // processBoostSDProduct may already have removed disabled, in which case
                // the old conditional check never fired → button fell back to Boost's Add to Cart.
                btn.classList.add('notify-me-btn');

                // Remove disabled if still present
                if (btn.hasAttribute('disabled')) {
                    btn.removeAttribute('disabled');
                }

                console.log(`[NotifyMe]   ✅ Phone injected & .notify-me-btn set`, {
                    productId: btn.dataset.productId,
                    variantId: btn.dataset.variantId,
                    sku: btn.dataset.sku,
                    phone
                });

                // Check if already subscribed
                if (subbedVariantIds.includes(String(btn.dataset.variantId))) {
                    btn.classList.add('subscribed');
                    if (btn.classList.contains('plp-nofify-me-btn')) {
                        updateButtonText(btn, 'Notified');
                    }
                    // Feedback handled on click in handleCTAClick
                }
            }
        });
    }

    /**
     * Wait for the full page to load (including Boost SD scripts + product grid render),
     * then poll every 500ms until all Notify Me buttons have a phone number.
     *
     * Strategy:
     *  1. Wait for window "load" event (all JS/CSS/images done)
     *  2. Add an extra 1500ms delay so Boost SD can render its product grid
     *  3. Poll every 500ms, up to 10 times (5 seconds total)
     */
    function startPhoneInjectionPoller() {
        function runPoller() {
            console.log('[NotifyMe] startPhoneInjectionPoller — page fully loaded, waiting 1500ms for Boost SD to render buttons...');

            setTimeout(() => {
                console.log('[NotifyMe] startPhoneInjectionPoller — starting poll loop now');
                let attempts = 0;
                const MAX_ATTEMPTS = 10;
                const INTERVAL = 500;

                const poller = setInterval(() => {
                    attempts++;
                    console.log(`[NotifyMe] Phone injection poll attempt ${attempts}/${MAX_ATTEMPTS}`);
                    injectPhoneToNotifyButtons();

                    // Stop once all buttons are filled, or we hit the limit
                    const remaining = document.querySelectorAll('button.boost-sd__button.plp-add-btn.boost-sd__button--select-option[data-phone-no=""]');
                    console.log(`[NotifyMe]   → ${remaining.length} button(s) still missing phone number`);

                    if (remaining.length === 0 || attempts >= MAX_ATTEMPTS) {
                        clearInterval(poller);
                        console.log(`[NotifyMe] ✅ Poller done after ${attempts} attempt(s). Remaining empty: ${remaining.length}`);
                    }
                }, INTERVAL);

            }, 1500); // 1.5s delay after window.load
        }

        // Wait for everything (including Boost SD JS) to finish loading
        if (document.readyState === 'complete') {
            // Already fully loaded (e.g. script is deferred and runs late)
            runPoller();
        } else {
            window.addEventListener('load', runPoller);
        }
    }

    /**
     * Watch the Boost SD product grid for pagination changes.
     * When the user clicks a page number, Boost SD replaces the product list children.
     * We detect that via MutationObserver and re-inject the phone into the new buttons.
     */
    function initPaginationObserver() {
        const productGrid = document.querySelector('.boost-sd__product-list');

        if (!productGrid) {
            console.log('[NotifyMe] initPaginationObserver — .boost-sd__product-list not found, skipping');
            return;
        }

        console.log('[NotifyMe] initPaginationObserver — watching product grid for page changes');

        const observer = new MutationObserver(() => {
            console.log('[NotifyMe] 📄 Pagination change detected — re-injecting phone & marking subscribed buttons after 800ms');
            setTimeout(() => {
                injectPhoneToNotifyButtons();
                // For guest users: subbedVariantIds is already populated from init;
                // re-mark buttons on the newly rendered page
                if (subbedVariantIds.length > 0) {
                    markSubscribedButtons();
                }
            }, 800); // Wait for Boost SD to finish rendering the new page's products
        });

        observer.observe(productGrid, {
            childList: true,  // fires when product items are added/removed (page change)
            subtree: false    // only direct children, not deep DOM changes
        });
    }

    // Public API
    return {
        init: () => {
            if (window.__ZYDUS_NOTIFY_ME_INIT_DONE__) {
                console.log('[NotifyMe] Already initialized, refreshing visibility only');
                setInitialButtonVisibility();
                markSubscribedButtons();
                syncAllSubmitStates();
                return;
            }
            window.__ZYDUS_NOTIFY_ME_INIT_DONE__ = true;
            init();
            initBoostSDObserver();
            startPhoneInjectionPoller();
            initPaginationObserver();
        },
        openForVariant: openForVariant,
        handleCTAClick: handleCTAClick,
        injectPhoneToNotifyButtons: injectPhoneToNotifyButtons
    };
})();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.NotifyMe.init);
} else {
    window.NotifyMe.init();
}
